import React, { createContext, useCallback, useContext } from 'react';
import { useQueryParam, StringParam } from 'use-query-params';
import { useDispatch, useSelector } from 'react-redux';
import { clearCatalog, resetPageNav } from '../../../store/reducers/catalogSlice';
import {
  getAlbumInfo,
  getAlbums,
  getCollections,
  getPlaylists,
  getTracks,
} from '../../../store/actions/catalogActions';

export const CatalogContext = createContext(null);
export const useCatalog = () => useContext(CatalogContext);

const CatalogContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { trackSearch } = useSelector((state) => state.catalog);

  const [type = 'tracks', setType] = useQueryParam('type', StringParam);
  const [collectionId, setCollectionId] = useQueryParam('collection', StringParam);
  const [albumId, setAlbumId] = useQueryParam('album', StringParam);
  const [playlistId, setPlaylistId] = useQueryParam('playlist', StringParam);
  const [trackId, setTrackId] = useQueryParam('trackId', StringParam);
  const [sort, setSort] = useQueryParam('sort', StringParam);

  const setPageHandler = useCallback(
    (value) => {
      setType && setType(value);
      setCollectionId && setCollectionId(undefined);
      setAlbumId && setAlbumId(undefined);
      setPlaylistId && setPlaylistId(undefined);
      setTrackId && setTrackId(undefined);
      setSort && setSort(undefined);

      dispatch(clearCatalog());
      dispatch(resetPageNav());
    },
    [dispatch, setType, setCollectionId, setAlbumId, setPlaylistId, setTrackId, setSort]
  );

  const showFilteredTracks = useCallback(() => {
    const isDetailPage = Boolean(albumId || playlistId);
    if (!isDetailPage && (type !== 'tracks' || trackId)) {
      setPageHandler('tracks');
    }
  }, [albumId, playlistId, setPageHandler, trackId, type]);

  const getCurrentPageData = useCallback(
    ({ page, filters, infinity, name }) => {
      switch (true) {
        case type === 'collections' && !albumId:
          if (collectionId) {
            dispatch(
              getAlbums({ page, filters, collectionId, sort, name, infinity })
            );
          } else {
            dispatch(getCollections({ page, filters, name, infinity }));
          }
          break;

        case type === 'albums' && !albumId:
          dispatch(getAlbums({ page, filters, sort, name, infinity }));
          break;

        case type === 'playlists' && !playlistId:
          dispatch(getPlaylists({ page, filters, name, infinity }));
          break;

        case type === 'tracks' && !trackId:
        case type === 'collections' && !!albumId:
        case type === 'albums' && !!albumId:
          if (!trackSearch.length || infinity) {
            dispatch(
              getTracks({
                page,
                filters,
                album_id: albumId,
                sort,
                name,
                infinity,
              })
            );
            if (page === 1 && albumId) {
              dispatch(getAlbumInfo({ id: albumId }));
            }
          }
          break;

        default:
          break;
      }
    },
    [type, albumId, collectionId, playlistId, trackId, sort, trackSearch, dispatch]
  );

  // ✅ безопасные setter функции
  const setAlbum = useCallback(
    (id) => {
      setAlbumId && setAlbumId(id);
      dispatch(clearCatalog());
      dispatch(resetPageNav());
      dispatch(getTracks({ page: 1, album_id: id }));
      dispatch(getAlbumInfo({ id }));
    },
    [dispatch, setAlbumId]
  );

  const setCollection = useCallback(
    (id) => {
      setCollectionId && setCollectionId(id);
      dispatch(clearCatalog());
      dispatch(resetPageNav());
      dispatch(getAlbums({ page: 1, collectionId: id }));
    },
    [dispatch, setCollectionId]
  );

  const setPlaylist = useCallback(
    (id) => {
      setPlaylistId && setPlaylistId(id);
      dispatch(clearCatalog());
      dispatch(resetPageNav());
      dispatch(getPlaylists({ page: 1, playlistId: id }));
    },
    [dispatch, setPlaylistId]
  );

  return (
    <CatalogContext.Provider
      value={{
        type,
        setType,
        setPageHandler,
        showFilteredTracks,
        collectionId,
        setCollectionId,
        albumId,
        setAlbumId,
        playlistId,
        setPlaylistId,
        trackId,
        setTrackId,
        sort,
        setSort,
        getCurrentPageData,
        setAlbum,
        setCollection,
        setPlaylist,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export default CatalogContextProvider;
